import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { Container, Button, Spinner, Alert, Form, Modal } from "react-bootstrap";
import { DestinazioneContext } from "../context/DestinazioneContext";
import Calendar from "react-calendar"; // Importa il componente Calendar
import "react-calendar/dist/Calendar.css"; // Importa lo stile del calendario
import orderService from "../services/orderService";
import OrderTable from "../components/OrderTable";
import ArticleTable from "../components/ArticleTable";
import { ThemeContext } from '../context/ThemeContext';
import '@fortawesome/fontawesome-free/css/all.css';
import { useAlert } from "../context/AlertContext";
import configService from "../services/configService";
import {
  parseConfiguratorTimeToMinutes,
  formatConfiguratorOrarioDisplay,
  isDestinationBlockCutoffPassed,
} from "../utils/configuratorOrarioDisplay";
import { europeRomeBakeInstantUtcMs } from "../utils/europeRomeTime";
import { composeDescrizArt, splitDescrizAndNota } from "../utils/ordineStraordinarioNota";
import {
  arricchisciOrdineConPrezzoCatalogo,
  mappaPrezziCatalogo,
  mostraPrezziDestinazione,
} from "../utils/mostraPrezziDestinazione";

// Helper per ottenere la data corrente nel fuso orario italiano
const getItalianDate = () => {
  const date = new Date();
  const options = { timeZone: 'Europe/Rome' };
  const italianDateString = date.toLocaleString('en-US', options);
  return new Date(italianDateString);
};

// Helper per ottenere il giorno della settimana da una data
const getDayOfWeekFromDate = (date) => {
  const dayOfWeek = date.getDay(); // 0 = domenica, 1 = lunedì, ..., 6 = sabato
  const dayMapping = {
    0: "domenica",
    1: "lunedi",
    2: "martedi",
    3: "mercoledi",
    4: "giovedi",
    5: "venerdi",
    6: "sabato"
  };
  return dayMapping[dayOfWeek];
};

/** Data locale Italia (Europe/Rome) in YYYY-MM-DD */
const dateToItalianYmd = (d) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d instanceof Date ? d : new Date(d));

/** Normalizza data_ord API in YYYY-MM-DD per confronti e filtri. */
const normalizeOrderDateYmd = (raw) => {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const loose = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (loose) {
    return `${loose[1]}-${String(loose[2]).padStart(2, '0')}-${String(loose[3]).padStart(2, '0')}`;
  }
  const it = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (it) {
    return `${it[3]}-${String(it[2]).padStart(2, '0')}-${String(it[1]).padStart(2, '0')}`;
  }
  return s;
};

/** Aggiunge giorni a una data YYYY-MM-DD (calendario gregoriano sui numeri di data) */
const addDaysToYmd = (ymd, deltaDays) => {
  const [y, m, day] = ymd.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, day + deltaDays));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
};

/** Chiave giorno (lunedi…domenica) del giorno successivo rispetto a oggi nel fuso Italia (getItalianDate). */
const getTomorrowWeekdayKeyItaly = () => {
  const todayItalian = getItalianDate();
  const tomorrow = new Date(todayItalian);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return getDayOfWeekFromDate(tomorrow);
};

/** Ordine straordinario con data = domani (IT), oppure vista settimanale sul giorno che coincide con domani (IT). */
const isOrderContextTomorrowItaly = (specificDate, selectedDay) => {
  if (specificDate) {
    const tomorrowY = addDaysToYmd(dateToItalianYmd(new Date()), 1);
    return dateToItalianYmd(specificDate) === tomorrowY;
  }
  return selectedDay === getTomorrowWeekdayKeyItaly();
};

const MSG_FASCIA_DOMANI =
  'In questa fascia oraria non è consentito modificare ordini riferiti a domani.';
const MSG_ARTICLE_MIN_PROD =
  'Questo articolo ha un tempo minimo di produzione: non è possibile modificare le quantità per i giorni ancora coperti da quell’anticipo.';

/** Ore da dati_articolo / data_forn_arti (planning standard vs straordinario). */
const getOreBloccoProd = (row) => {
  const raw =
    row?.dati_articolo?.orebloccoprod ??
    row?.data_forn_arti?.orebloccoprod ??
    row?.orebloccoprod;
  const n = Number(String(raw ?? '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const WEEKDAY_KEY_ORDER = [
  'lunedi',
  'martedi',
  'mercoledi',
  'giovedi',
  'venerdi',
  'sabato',
  'domenica',
];

/** getUTCDay(): 0 = domenica … 6 = sabato (stesso schema di getDayOfWeekFromDate). */
const weekdayKeyToJsSunday0 = (dayKey) => {
  const m = {
    domenica: 0,
    lunedi: 1,
    martedi: 2,
    mercoledi: 3,
    giovedi: 4,
    venerdi: 5,
    sabato: 6,
  };
  return m[dayKey];
};

const jsSunday0FromItalianYmd = (ymd) => {
  const [y, mo, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
};

/**
 * Prossima data di consegna (YYYY-MM-DD, calendario Italia) con quel nome giorno, da **oggi** in poi
 * (0 = oggi se la colonna è proprio oggi). Così di venerdì le colonne lun–gio puntano alla settimana
 * successiva, come nella prospettiva operativa descritta (non al lunedì ISO della settimana corrente).
 */
const italianDeliveryYmdForWeekdayKey = (dayKey) => {
  const targetDow = weekdayKeyToJsSunday0(dayKey);
  if (targetDow === undefined) return null;
  const todayYmd = dateToItalianYmd(new Date());
  const todayDow = jsSunday0FromItalianYmd(todayYmd);
  const delta = (targetDow - todayDow + 7) % 7;
  return addDaysToYmd(todayYmd, delta);
};

/**
 * Epoch ms del momento di produzione (forno) per la consegna deliveryYmd, in Europe/Rome.
 *
 * Regola operativa:
 * - se ORARIOPRODPANE < 07:00, l'orario appartiene al giorno di consegna (forno notturno/mattino presto);
 * - altrimenti appartiene al giorno civile precedente.
 * Minuti da mezzanotte Roma del bakeYmd (overflow come Date locale precedente).
 */
const bakeInstantUtcMsBeforeDeliveryYmd = (deliveryYmd, prodMinutes) => {
  if (prodMinutes == null) return null;
  const sevenAmMinutes = 7 * 60;
  const bakeYmd =
    prodMinutes < sevenAmMinutes
      ? deliveryYmd
      : addDaysToYmd(deliveryYmd, -1);
  const ms = europeRomeBakeInstantUtcMs(bakeYmd, prodMinutes);
  return Number.isNaN(ms) ? null : ms;
};

/** True se non si possono più cambiare quantità per consegna deliveryYmd (istante “adesso” = Date.now(), bake in Europe/Rome). */
const isDeliveryYmdLockedByOreBloccoProd = (deliveryYmd, row, orarioProdPaneRaw) => {
  if (typeof window !== 'undefined' && localStorage.getItem('isSuperuser') === 'true') {
    return false;
  }
  const ore = getOreBloccoProd(row);
  if (ore <= 0) return false;
  const prodMinutes = parseConfiguratorTimeToMinutes(orarioProdPaneRaw);
  if (prodMinutes == null) return false;
  const bakeMs = bakeInstantUtcMsBeforeDeliveryYmd(deliveryYmd, prodMinutes);
  if (bakeMs == null || Number.isNaN(bakeMs)) return false;
  const cutoffMs = bakeMs - ore * 60 * 60 * 1000;
  return Date.now() >= cutoffMs;
};

const isWeekdayColumnLockedForArticle = (dayKey, row, orarioProdPaneRaw) => {
  const ymd = italianDeliveryYmdForWeekdayKey(dayKey);
  if (!ymd) return false;
  return isDeliveryYmdLockedByOreBloccoProd(ymd, row, orarioProdPaneRaw);
};

const normalizeUnitaMisura = (orderData) =>
  orderData.unitaMisura ||
  orderData.unita_m ||
  orderData.unita ||
  (orderData.data_forn_arti && orderData.data_forn_arti.unita_m);

/** Riga tabella da risposta API straordinari (stesso shape usato in handleDateChange). */
const mapApiExtraOrderToRow = (order, { previewId = false } = {}) => {
  const { descriz, nota } = splitDescrizAndNota(order.descriz_art);
  return {
    id: previewId ? `extra-${order.id}` : order.id,
    codice: order.cod_arti,
    descriz,
    nota,
    unitaMisura: normalizeUnitaMisura(order),
    data_forn_arti: order.data_forn_arti || null,
    dati_articolo: order.data_forn_arti || null,
    orebloccoprod: order.data_forn_arti?.orebloccoprod,
    peso: order.data_forn_arti?.peso_lo,
    qtaMinima: order.data_forn_arti?.qtaminima,
    qtaRange: order.data_forn_arti?.qtarange,
    note_est: order.data_forn_arti?.note_est || '',
    quantita: order.quanti,
    lunedi: 0,
    martedi: 0,
    mercoledi: 0,
    giovedi: 0,
    venerdi: 0,
    sabato: 0,
    domenica: 0,
  };
};

/** Etichetta data lunga (it-IT) da YYYY-MM-DD calendario locale. */
const formatItalianLongDateFromYmd = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const s = dt.toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

function Planning({ selectedDay, setSelectedDay, sidebarVisible = false, isMobile = false }) {

  // variabili di stato e contesto
  const { showAlertGlobal } = useAlert(); // Stato per gestire la visibilità dell'alert

  const { darkMode } = useContext(ThemeContext);
  const { destinazione, dayActivationFlags, updateDayActivationFlag } = useContext(DestinazioneContext);
  
  // Stato per tracciare se i dati del context sono stati caricati
  const [contextDataLoaded, setContextDataLoaded] = useState(false);
  const [selettoreGruppo, setSelettoreGruppo] = useState(1); // Default a 1

  
  const [mappedOrders, setMappedOrders] = useState([]); // Stato unico per gli ordini
  /** Anteprima lettura: straordinari per la prossima data di consegna del giorno selezionato (vista settimanale). */
  const [previewExtraOrdini, setPreviewExtraOrdini] = useState([]);
  const [previewExtraDeliveryYmd, setPreviewExtraDeliveryYmd] = useState(null);
  const [articles, setArticles] = useState([]);
  const catalogoPrezziPerCodice = useMemo(() => mappaPrezziCatalogo(articles), [articles]);
  const catalogoPrezziRef = useRef(catalogoPrezziPerCodice);
  catalogoPrezziRef.current = catalogoPrezziPerCodice;
  const applicaPrezzoCatalogo = useCallback(
    (row) => arricchisciOrdineConPrezzoCatalogo(row, catalogoPrezziRef.current),
    []
  );
  const mostraPrezzi = mostraPrezziDestinazione(destinazione);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [errorOrders, setErrorOrders] = useState(null);
  const [errorArticles, setErrorArticles] = useState(null);

  const [showModal, setShowModal] = useState(false); // Stato per gestire la visibilità del modal
  // PANIFYNOPLANNING: null = config non ancora caricata, true/false = modalità senza planning
  const [noPlanning, setNoPlanning] = useState(null);
  const [specificDate, setSpecificDate] = useState(null); // Stato per la data selezionata nel modal
  const [specificDateDay, setSpecificDateDay] = useState(null); // Stato per il giorno della settimana della data selezionata
  const [selectedDate, setSelectedDate] = useState(getItalianDate()); // Stato per la data selezionata
  const [highlightedDates, setHighlightedDates] = useState([]); // Stato per le date evidenziate
  const [zeroQuantityDates, setZeroQuantityDates] = useState([]); // Stato per le date con quantità zero
  const [extraOrderActiveDates, setExtraOrderActiveDates] = useState([]); // Date con ordine (somma quanti > 0)
  const [aperturaChiusura, setAperturaChiusura] = useState(true); // Stato per gestire apertura/chiusura straordinaria
  const allOrdersSuspended = mappedOrders.every((order) => Number(order.quantita) === 0);

  const [showChiusuraModal, setShowChiusuraModal] = useState(false);
  const [selectedZeroDate, setSelectedZeroDate] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  const [showAddProductsModal, setShowAddProductsModal] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [categorie, setCategorie] = useState([]);
  const [selectedCategoria, setSelectedCategoria] = useState("");

  const [loading, setLoading] = useState(false);

  // Stato per la gestione delle restrizioni orarie
  const [orarioValido, setOrarioValido] = useState(null);
  const [orarioBlocco, setOrarioBlocco] = useState(null);
  const [orarioSblocco, setOrarioSblocco] = useState(null);
  const [useCustomBlockWindow, setUseCustomBlockWindow] = useState(false);
  /** Tick per rivalutare la fascia oraria sul calendario e sul blocco modifiche “domani”. */
  const [calendarRestrictionTick, setCalendarRestrictionTick] = useState(0);

  // Stato per nascondere/mostrare prodotti con quantità 0
  const [hideZeroQuantity, setHideZeroQuantity] = useState(false);
  const [toolbarPinnedTop, setToolbarPinnedTop] = useState(false);
  const [toolbarHeight, setToolbarHeight] = useState(40);
  const ordersDockRef = useRef(null);
  const productToolbarRef = useRef(null);
  // Ricerca per descrizione: posiziona la vista sulla riga del prodotto trovato
  const [cercaDescrizione, setCercaDescrizione] = useState("");

  useEffect(() => {
    if (!isMobile) {
      setToolbarPinnedTop(false);
      return undefined;
    }

    const updateToolbarPin = () => {
      const dock = ordersDockRef.current;
      const toolbar = productToolbarRef.current;
      if (!dock || !toolbar) {
        return;
      }
      const styles = getComputedStyle(document.documentElement);
      const navbar = parseFloat(styles.getPropertyValue('--navbar-mobile')) || 44;
      const hasOrario = Boolean(document.querySelector('.app-container.navbar-has-orario-bar'));
      const orario = hasOrario ? (parseFloat(styles.getPropertyValue('--orario-strip')) || 32) : 0;
      const pinTop = navbar + orario;
      const height = toolbar.offsetHeight;
      setToolbarHeight(height);
      setToolbarPinnedTop(dock.getBoundingClientRect().bottom <= pinTop + height + 1);
    };

    updateToolbarPin();
    window.addEventListener('scroll', updateToolbarPin, { passive: true });
    window.addEventListener('resize', updateToolbarPin);
    return () => {
      window.removeEventListener('scroll', updateToolbarPin);
      window.removeEventListener('resize', updateToolbarPin);
    };
  }, [isMobile, mappedOrders.length, loadingOrders, hideZeroQuantity]);

  // Finestra di blocco: override destinazione (abilita_blocco + orarioblocco_dest, non superuser),
  // altrimenti ORARIOBLOCCO / ORARIOSBLOCCO se entrambi valorizzati, altrimenti ORARIOPRODPANE + 08:00
  const checkTimeRestrictions = useCallback(() => {
    const isSuper =
      typeof window !== 'undefined' && localStorage.getItem('isSuperuser') === 'true';

    let closeRaw = null;
    let isDestOverride = false;
    if (!isSuper && destinazione?.datiDestinazione) {
      const ab = String(destinazione.datiDestinazione.abilita_blocco ?? '').trim();
      if (ab === '1') {
        const dr = destinazione.datiDestinazione.orarioblocco_dest;
        if (dr != null && String(dr).trim() !== '') {
          const pm = parseConfiguratorTimeToMinutes(dr);
          if (pm != null) {
            closeRaw = dr;
            isDestOverride = true;
          }
        }
      }
    }
    if (closeRaw === null || closeRaw === '') {
      closeRaw = useCustomBlockWindow ? orarioBlocco : orarioValido;
    }
    if (closeRaw === null || closeRaw === undefined || closeRaw === '') {
      return false;
    }

    let openTotalMinutes = 8 * 60;
    if (useCustomBlockWindow && orarioSblocco != null && String(orarioSblocco).trim() !== '') {
      const parsedOpen = parseConfiguratorTimeToMinutes(orarioSblocco);
      if (parsedOpen != null) {
        openTotalMinutes = parsedOpen;
      }
    }

    const restrictionTotalMinutes = parseConfiguratorTimeToMinutes(closeRaw);
    if (restrictionTotalMinutes == null) {
      return false;
    }

    try {
      const formatter = new Intl.DateTimeFormat('it-IT', {
        timeZone: 'Europe/Rome',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      });

      const parts = formatter.formatToParts(new Date());
      const hourPart = parts.find((p) => p.type === 'hour')?.value || '0';
      const minutePart = parts.find((p) => p.type === 'minute')?.value || '0';

      const currentTotalMinutes =
        parseInt(hourPart, 10) * 60 + parseInt(minutePart, 10);

      // Override destinazione: cutoff con regole notturne/mattutine (isDestinationBlockCutoffPassed).
      if (isDestOverride) {
        return isDestinationBlockCutoffPassed(
          currentTotalMinutes,
          restrictionTotalMinutes
        );
      }

      if (restrictionTotalMinutes > openTotalMinutes) {
        return (
          currentTotalMinutes >= restrictionTotalMinutes ||
          currentTotalMinutes < openTotalMinutes
        );
      }
      return (
        currentTotalMinutes >= restrictionTotalMinutes &&
        currentTotalMinutes < openTotalMinutes
      );
    } catch (e) {
      console.error('Errore checkTimeRestrictions:', e);
      return false;
    }
  }, [useCustomBlockWindow, orarioValido, orarioBlocco, orarioSblocco, destinazione]);

  const hasDestinationBlockOverride = useMemo(() => {
    if (typeof window === 'undefined') return false;
    if (localStorage.getItem('isSuperuser') === 'true') return false;
    const dd = destinazione?.datiDestinazione;
    if (!dd) return false;
    if (String(dd.abilita_blocco ?? '').trim() !== '1') return false;
    const raw = dd.orarioblocco_dest;
    if (raw == null || String(raw).trim() === '') return false;
    return parseConfiguratorTimeToMinutes(raw) != null;
  }, [destinazione]);

  /** Orario “chiusura” mostrato nel messaggio fascia (override destinazione vs globale / fallback prod). */
  const fasciaCloseLabel = useMemo(() => {
    if (hasDestinationBlockOverride && destinazione?.datiDestinazione?.orarioblocco_dest) {
      return formatConfiguratorOrarioDisplay(destinazione.datiDestinazione.orarioblocco_dest);
    }
    if (useCustomBlockWindow && orarioBlocco != null) {
      return formatConfiguratorOrarioDisplay(orarioBlocco);
    }
    return formatConfiguratorOrarioDisplay(orarioValido);
  }, [hasDestinationBlockOverride, destinazione, useCustomBlockWindow, orarioBlocco, orarioValido]);

  const hasTimeRestrictionConfig =
    hasDestinationBlockOverride ||
    useCustomBlockWindow ||
    (orarioValido !== null &&
      orarioValido !== undefined &&
      orarioValido !== '');

  const calendarBlocksTodayTomorrowItaly = useMemo(() => {
    if (typeof window === 'undefined') return false;
    if (localStorage.getItem('isSuperuser') === 'true') return false;
    if (!hasTimeRestrictionConfig) return false;
    return checkTimeRestrictions();
  }, [hasTimeRestrictionConfig, checkTimeRestrictions, calendarRestrictionTick]);

  /** In fascia oraria di blocco: niente modifiche ai valori riferiti a “domani” (tabella, modale, salvataggi). */
  const planningTomorrowModificationsDisabled = useMemo(() => {
    if (typeof window === 'undefined') return false;
    if (localStorage.getItem('isSuperuser') === 'true') return false;
    if (!hasTimeRestrictionConfig) return false;
    if (!checkTimeRestrictions()) return false;
    // Blocco totale SOLO per ordini straordinari sulla data di domani (l'intero ordine è per domani).
    // Nel planning settimanale il blocco è granulare: si blocca solo la colonna "domani"
    // (via readOnlyWeekdayKeysForModal), il resto del planning resta modificabile.
    if (!specificDate) return false;
    return isOrderContextTomorrowItaly(specificDate, selectedDay);
  }, [
    hasTimeRestrictionConfig,
    checkTimeRestrictions,
    calendarRestrictionTick,
    specificDate,
    selectedDay,
  ]);

  const readOnlyWeekdayKeysForModal = useMemo(() => {
    // In fascia: blocca sempre la colonna "domani" (IT) nel modale / griglia, anche se selectedDay
    // non coincide con domani (es. mercoledì 17:30 → giovedì read-only). Non mescolare con lo
    // straordinario (specificDate): lì vale solo orebloccoprod sulla data scelta.
    if (specificDate) return [];
    if (!calendarBlocksTodayTomorrowItaly) return [];
    return [getTomorrowWeekdayKeyItaly()];
  }, [specificDate, calendarBlocksTodayTomorrowItaly, calendarRestrictionTick]);

  /** Giorni precedenti con ordine personalizzato (esclusi chiusure), selezionabili per duplicazione. */
  const eligibleDuplicateDates = useMemo(() => {
    if (!specificDate) return new Set();
    const currentYmd = dateToItalianYmd(specificDate);
    return new Set(
      extraOrderActiveDates.filter((ymd) => ymd && ymd < currentYmd)
    );
  }, [specificDate, extraOrderActiveDates]);

  const hasEligibleDuplicateDates = eligibleDuplicateDates.size > 0;

  const getExtraReadOnlyKeysForArticleCb = useCallback(
    (art) => {
      if (typeof window !== 'undefined' && localStorage.getItem('isSuperuser') === 'true') {
        return [];
      }
      if (orarioValido == null || String(orarioValido).trim() === '') return [];
      return WEEKDAY_KEY_ORDER.filter((k) => isWeekdayColumnLockedForArticle(k, art, orarioValido));
    },
    [orarioValido, calendarRestrictionTick]
  );

  const getRowLockedForTodayTomorrowCb = useCallback(
    (art) => {
      if (typeof window !== 'undefined' && localStorage.getItem('isSuperuser') === 'true') {
        return false;
      }
      if (orarioValido == null || String(orarioValido).trim() === '') return false;
      if (specificDate) {
        const d = dateToItalianYmd(specificDate);
        return isDeliveryYmdLockedByOreBloccoProd(d, art, orarioValido);
      }
      const ymd = italianDeliveryYmdForWeekdayKey(selectedDay);
      if (!ymd) return false;
      return isDeliveryYmdLockedByOreBloccoProd(ymd, art, orarioValido);
    },
    [specificDate, selectedDay, orarioValido, calendarRestrictionTick]
  );

  // Carica ORARIOPRODPANE + ORARIOBLOCCO / ORARIOSBLOCCO all'avvio
  useEffect(() => {
    const loadTimeRestrictions = async () => {
      try {
        const [blocco, sblocco, orario] = await Promise.all([
          configService.getOrarioBlocco(),
          configService.getOrarioSblocco(),
          configService.getOrarioValido(),
        ]);
        setOrarioValido(orario);
        const bOk = blocco != null && String(blocco).trim() !== '';
        const sOk = sblocco != null && String(sblocco).trim() !== '';
        setOrarioBlocco(bOk ? blocco : null);
        setOrarioSblocco(sOk ? sblocco : null);
        setUseCustomBlockWindow(bOk && sOk);
      } catch (error) {
        console.error('Errore nel caricamento degli orari di blocco:', error);
      }
    };

    loadTimeRestrictions();
  }, []);

  // Aggiorna ogni minuto la possibilità di selezionare oggi/domani sul calendario (fascia oraria)
  useEffect(() => {
    if (!hasTimeRestrictionConfig) return undefined;
    const id = setInterval(() => {
      setCalendarRestrictionTick((n) => n + 1);
    }, 60000);
    return () => clearInterval(id);
  }, [hasTimeRestrictionConfig]);

  // Aggiungere questo useEffect per caricare il selettore gruppo
  useEffect(() => {
    const loadSelettoreGruppo = async () => {
      try {
        const gruppo = await configService.getSelettoreGruppo();
        setSelettoreGruppo(gruppo || 1); // Default a 1 se null o 0
      } catch (error) {
        console.error('Errore nel caricamento del selettore gruppo:', error);
        setSelettoreGruppo(1); // Fallback al default
      }
    };

    loadSelettoreGruppo();
  }, []);

  // Carica il flag PANIFYNOPLANNING (1 = clic giorno apre direttamente la data specifica più vicina)
  useEffect(() => {
    let active = true;
    configService
      .getPanifyNoPlanning()
      .then((value) => {
        if (active) setNoPlanning(Boolean(value));
      })
      .catch(() => {
        if (active) setNoPlanning(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // ...funzione di filtro unica...
  const filteredArticles = articles.filter(article => {
    // Trova l'articolo in mappedOrders
    const orderFound = mappedOrders.find(order => order.codice === article.cd_min);
    
    // Mostra l'articolo se:
    // 1. Non è presente in mappedOrders, OPPURE
    // 2. È presente ma ha quantità = 0
    const notInOrdersOrZeroQuantity = !orderFound ;

    // Se entrambi i filtri sono vuoti, mostra tutto (escludendo già presenti con quantità > 0)
    if (searchText.trim() === "" && selectedCategoria === "") {
      return notInOrdersOrZeroQuantity;
    }

    // Filtro testo su cd_min o descriz
    const testo = searchText.toLowerCase();
    const matchTesto =
      article.cd_min.toLowerCase().includes(testo) ||
      article.descriz.toLowerCase().includes(testo);

    // Filtro categoria DINAMICO basato sul selettore gruppo
    let matchCategoria = true;
    if (selectedCategoria) {
      switch (selettoreGruppo) {
        case 1:
          matchCategoria = article.colore === selectedCategoria;
          break;
        case 2:
          matchCategoria = article.tipo_arti === selectedCategoria;
          break;
        case 3:
          matchCategoria = article.ngruppo3 === selectedCategoria;
          break;
        case 4:
          matchCategoria = article.gruppo5 === selectedCategoria; // Assumo che gruppo 4 corrisponda a gruppo5
          break;
        case 5:
          matchCategoria = article.gruppo6 === selectedCategoria; // Assumo che gruppo 5 corrisponda a gruppo6
          break;
        case 6:
          matchCategoria = article.mgruppo7 === selectedCategoria; // Assumo che gruppo 6 corrisponda a mgruppo7
          break;
        default:
          matchCategoria = article.colore === selectedCategoria; // Fallback al gruppo 1
          break;
      }
    }

    return matchTesto && matchCategoria && notInOrdersOrZeroQuantity;
  });

  // Definizione di filteredArticles
  /*
  const filteredArticles = articles.filter(
    (article) => !mappedOrders.some((order) => order.codice === article.cd_min)
  );
  */

  // Funzione per caricare gli ordini standard
  // Questa funzione viene chiamata all'apertura del componente 
  const loadOrders = useCallback(async () => {
    if (!destinazione) {
      setPreviewExtraOrdini([]);
      setPreviewExtraDeliveryYmd(null);
      return;
    }

    setLoadingOrders(true);
    setErrorOrders(null);

    const deliveryYmd = italianDeliveryYmdForWeekdayKey(selectedDay);

    try {
      const [data, extraRaw] = await Promise.all([
        orderService.getOrdineStandard(selectedDay),
        deliveryYmd
          ? orderService.getOrdineExtra(deliveryYmd).catch(() => [])
          : Promise.resolve([]),
      ]);

      const mappedOrders = data.map((order) => applicaPrezzoCatalogo({
        id: order.id,
        codice: order.codice,
        descriz: order.descriz,
        unitaMisura: normalizeUnitaMisura(order),
        quantita: order[selectedDay],
        dati_articolo: order.dati_articolo || null,
        orebloccoprod: order.dati_articolo?.orebloccoprod,
        peso: order.dati_articolo?.peso_lo || 0,
        qtaMinima: order.dati_articolo?.qtaminima || 0,
        qtaRange: order.dati_articolo?.qtarange || 0,
        note_est: order.dati_articolo?.note_est || order.note_est || '', // Aggiungi note_est dal dato articolo
        lunedi: order.lunedi || 0,
        martedi: order.martedi || 0,
        mercoledi: order.mercoledi || 0,
        giovedi: order.giovedi || 0,
        venerdi: order.venerdi || 0,
        sabato: order.sabato || 0,
        domenica: order.domenica || 0,
      }));

      setMappedOrders(mappedOrders);

      const preview = (Array.isArray(extraRaw) ? extraRaw : [])
        .map((o) => applicaPrezzoCatalogo(mapApiExtraOrderToRow(o, { previewId: true })))
        .filter((r) => Number(r.quantita) > 0);
      setPreviewExtraOrdini(preview);
      setPreviewExtraDeliveryYmd(deliveryYmd);
    } catch (err) {
      setErrorOrders(`Errore nel caricamento degli ordini: ${err.message}`);
      setPreviewExtraOrdini([]);
      setPreviewExtraDeliveryYmd(null);
    } finally {
      setLoadingOrders(false);
    }
  }, [destinazione, selectedDay, applicaPrezzoCatalogo]);

  // PANIFYNOPLANNING: apre direttamente la data specifica (straordinario) più vicina del giorno,
  // riusando il flusso degli ordini straordinari. Non legge il planning settimanale.
  const openWeekdayAsSpecificDate = useCallback(async (dayKey) => {
    const ymd = italianDeliveryYmdForWeekdayKey(dayKey);
    if (!ymd) return;

    const [y, m, d] = ymd.split('-').map(Number);
    const dateObj = new Date(y, m - 1, d);

    setLoadingOrders(true);
    setErrorOrders(null);
    setSpecificDate(dateObj);
    setSpecificDateDay(dayKey);
    setSelectedDate(dateObj);

    try {
      const extraOrders = await orderService.getOrdineExtra(ymd);
      const rows = (Array.isArray(extraOrders) ? extraOrders : []).map((order) =>
        applicaPrezzoCatalogo(mapApiExtraOrderToRow(order, { previewId: false }))
      );
      // Lista vuota se non esiste alcun ordine per la data: l'utente può aggiungere articoli.
      setMappedOrders(rows);
      setPreviewExtraOrdini([]);
      setPreviewExtraDeliveryYmd(null);
    } catch (err) {
      setErrorOrders(`Errore nel caricamento degli ordini: ${err.message}`);
      setMappedOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, [applicaPrezzoCatalogo]);

  // Ricarica la vista corrente dopo salva/annulla:
  // - noPlanning: riapre la stessa data specifica del giorno selezionato
  // - planning standard: torna agli ordini ordinari
  const reloadView = useCallback(() => {
    if (noPlanning) {
      openWeekdayAsSpecificDate(selectedDay);
    } else {
      setSpecificDate(null);
      setSpecificDateDay(null);
      setSelectedDate(getItalianDate());
      loadOrders();
    }
  }, [noPlanning, selectedDay, openWeekdayAsSpecificDate, loadOrders]);

  const loadCategorie = async () => {
    try {
      const data = await orderService.getCategorieArticoli();
      setCategorie(data);
    } catch (err) {
    }
  };

  const handleOpenModal = async (isAperturaChiusura) => {
    setSpecificDate(null); // Resetta la data specifica
    setSpecificDateDay(null); // Resetta il giorno della data selezionata
    setAperturaChiusura(isAperturaChiusura); // Imposta il parametro apertura/chiusura
    
    // Ricarica le date evidenziate prima di mostrare il modal
    await loadHighlightedDates();
    
    setShowModal(true); // Mostra il modal
  };
  
  const handleCloseModal = () => {
    setShowModal(false); // Chiudi il modal
  };

  // Funzione per caricare gli articoli
  // Questa funzione viene chiamata all'apertura del componente
  const loadArticles = useCallback(async () => {
    if (!destinazione) return;

    setLoadingArticles(true);
    setErrorArticles(null);
    loadCategorie();

    try {
      const data = await orderService.getListaArticoli();
      setArticles(Array.isArray(data) ? data : []);
    } catch (err) {
      setErrorArticles(`Errore nel caricamento degli articoli: ${err.message}`);
    } finally {
      setLoadingArticles(false);
    }
  }, [destinazione]);

  // Funzione per salvare il planning
  // Questa funzione viene chiamata quando si salva un articolo con le quantità per il giorno selezionato
  const handleSavePlanning = async (selectedArticle, quantities, specificDateDayOverride = null) => {
    if (planningTomorrowModificationsDisabled) {
      showAlertGlobal(MSG_FASCIA_DOMANI, 'warning');
      return;
    }
    if (typeof window !== 'undefined' && localStorage.getItem('isSuperuser') !== 'true') {
      if (specificDate) {
        const d = dateToItalianYmd(specificDate);
        if (isDeliveryYmdLockedByOreBloccoProd(d, selectedArticle, orarioValido)) {
          const dayCol =
            specificDateDayOverride != null
              ? specificDateDayOverride
              : specificDateDay || selectedDay;
          const newQ = Number(quantities?.[dayCol] ?? 0);
          const oldQ = Number(selectedArticle?.[dayCol] ?? 0);
          if (newQ !== oldQ) {
            showAlertGlobal(MSG_ARTICLE_MIN_PROD, 'warning');
            return;
          }
        }
      } else {
        // Blocco fascia oraria (planning settimanale): la sola colonna "domani" non è modificabile.
        for (const day of readOnlyWeekdayKeysForModal) {
          const newQ = Number(quantities?.[day] ?? 0);
          const oldQ = Number(selectedArticle?.[day] ?? 0);
          if (newQ !== oldQ) {
            showAlertGlobal(MSG_FASCIA_DOMANI, 'warning');
            return;
          }
        }
        for (const day of WEEKDAY_KEY_ORDER) {
          if (!isWeekdayColumnLockedForArticle(day, selectedArticle, orarioValido)) continue;
          const newQ = Number(quantities?.[day] ?? 0);
          const oldQ = Number(selectedArticle?.[day] ?? 0);
          if (newQ !== oldQ) {
            showAlertGlobal(MSG_ARTICLE_MIN_PROD, 'warning');
            return;
          }
        }
      }
    }
    try {
      
      // Normalizza il campo unita usando la stessa logica di unitaMisura
      const unitaMisura = normalizeUnitaMisura(selectedArticle);

      const articoli = [
        {
          codice: selectedArticle.codice,
          descriz: selectedArticle.descriz,
          prezzo: selectedArticle.prezzo || 0,
          iva: selectedArticle.iva || "10",
          contropa: selectedArticle.contropa || "58050010",
          unita: unitaMisura || '', // Usa il valore normalizzato
          peso: selectedArticle.peso || 0,
          qtaMinima: selectedArticle.qtaminima || 0,
          qtaRange: selectedArticle.qtarange || 0, 
          ...quantities,
        },
      ];

      await orderService.salvaPlanning(articoli, false);
   

      setMappedOrders((mappedOrders) => {
        const existingOrder = mappedOrders.find((order) => order.codice === selectedArticle.codice);
        let updatedOrders;
        
        // Determina il giorno da usare: specificDateDay per ordini straordinari, selectedDay per ordini ordinari
        const dayToUse = specificDate ? (specificDateDay || selectedDay) : selectedDay;
        
        if (existingOrder) {
          updatedOrders = mappedOrders.map((order) =>
            order.codice === selectedArticle.codice
              ? { ...order, 
                  ...quantities,
                  quantita: quantities[dayToUse],
                  unitaMisura: unitaMisura,
               }
              : order
          );
        } else {
          // Aggiungi una nuova riga di planning ordine con le quantità per il giorno selezionato
          updatedOrders = [
            ...mappedOrders,
            {
              ...selectedArticle,
              ...quantities,
              quantita: quantities[dayToUse], // Imposta la quantità per il giorno selezionato
              unitaMisura: unitaMisura,
            },
          ];        
        }
        
        return updatedOrders;
      });
      showAlertGlobal("Planning salvato con successo!", "success"); // oppure "danger", "warning", ecc.
    } catch (err) {
      showAlertGlobal(`Errore nel salvataggio del planning: ${err.message}`, "danger"); // oppure "danger", "warning", ecc.
    }
  };


  const handleSuspendOrders = async () => {
    try {
      
      // Determina se il giorno è attualmente attivo ('0' = attivo, '1' = disattivo)
      let isDayCurrentlyActive = false; // Default: inattivo
      
      // Applica la regola semplice basata su selectedDay e destinazione.datiDestinazione
      if (destinazione?.datiDestinazione) {
        const dayMapping = {
          'lunedi': 'lun_act',
          'martedi': 'mar_act',
          'mercoledi': 'mer_act',
          'giovedi': 'gio_act',
          'venerdi': 'ven_act',
          'sabato': 'sab_act',
          'domenica': 'dom_act'
        };
        
        const dayField = dayMapping[selectedDay];
        if (dayField) {
          const flagValue = destinazione.datiDestinazione[dayField];
          isDayCurrentlyActive = flagValue === '0'; // CORRETTO: confronto con stringa '0'
          }
      }
      

      
      // Chiama il servizio per disabilitare/riattivare il giorno
      // true se il giorno è attivo e va disattivato, false se è disattivato e va riattivato
      await orderService.disabilitaGiorno(selectedDay, isDayCurrentlyActive);

      // Mostra messaggio di successo
      const action = isDayCurrentlyActive ? "disattivato" : "riattivato";
      showAlertGlobal(`Giorno ${selectedDay} ${action} con successo!`, "success");
      
      // Calcola il nuovo valore del flag
      const newFlagValue = isDayCurrentlyActive ? '1' : '0'; // CORRETTO: usa stringhe
      
      // Aggiorna il context in tempo reale (questo aggiorna automaticamente SideMenu e MobileDaySelector)
      updateDayActivationFlag(selectedDay, newFlagValue);
      
      console.log(`Aggiornato ${selectedDay} da ${isDayCurrentlyActive ? '0' : '1'} a ${newFlagValue}`);
      
    } catch (err) {
      showAlertGlobal(`Errore durante la modifica del giorno ${selectedDay}: ${err.message}`, "danger");
    }
  };


  const handleCancelExtraOrder = async (specificDate) => {
  if (!specificDate) {
    return;
  }

    try {
      // Formatta la data selezionata in formato "YYYY-MM-DD"
      const formattedDate = specificDate.toLocaleDateString("it-IT", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).split("/").reverse().join("-");

      // Passa la data come array
      await orderService.eliminaExtra([formattedDate]);
      showAlertGlobal("Ordine straordinario annullato con successo!", "success");
    } catch (error) {
      showAlertGlobal("Errore durante l'annullamento dell'ordine straordinario.", "danger");
    }
  };



  // Funzione per gestire la selezione della data nel calendario
  // Questa funzione viene chiamata quando si seleziona una data nel calendario
  const handleDateChange = async (dateSelected) => {
    const todayY = dateToItalianYmd(new Date());
    const tomorrowY = addDaysToYmd(todayY, 1);
    const selY = dateToItalianYmd(dateSelected);
    const blockOggiDomaniCal =
      typeof window !== 'undefined' &&
      localStorage.getItem('isSuperuser') !== 'true' &&
      hasTimeRestrictionConfig &&
      checkTimeRestrictions();
    if (blockOggiDomaniCal && (selY === todayY || selY === tomorrowY)) {
      showAlertGlobal(
        'In questa fascia oraria non è possibile selezionare oggi o domani dal calendario degli ordini straordinari.',
        'warning'
      );
      return;
    }

    setLoading(true); // Mostra il simbolo di caricamento

    setSelectedDate(dateSelected); // Aggiorna la data selezionata
    setSpecificDate(dateSelected); // Imposta la data specifica
    setShowModal(false); // Chiudi il modale

    try {
      const formattedDate = dateSelected.toLocaleDateString("it-IT", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).split("/").reverse().join("-"); // Converte "GG/MM/AAAA" in "AAAA-MM-GG"


      // Se la data è tra quelle con quantità zero, mostra il nuovo modal
      if (zeroQuantityDates.includes(formattedDate)) {
        setSelectedZeroDate(formattedDate);
        setShowChiusuraModal(true);
        return;
      }

      if (!aperturaChiusura) {
        
        // Se è una chiusura straordinaria, avvia la procedura di chiusura
        await orderService.chiusuraExtra([formattedDate]); // Passa la data come array
  
        showAlertGlobal("Chiusura straordinaria salvata con successo!", "success");

        setSpecificDate(null); // Resetta la data specifica per tornare agli ordini ordinari
        setSpecificDateDay(null); // Resetta il giorno della data selezionata
        setSelectedDate(getItalianDate()); // Ripristina la data di default
        loadOrders(); // Ricarica gli ordini ordinari
        
      } else {
        // Calcola il giorno della settimana della data selezionata
        const dayOfSelectedDate = getDayOfWeekFromDate(dateSelected);
        setSpecificDateDay(dayOfSelectedDate);
        
        // Carica gli ordini straordinari per la data selezionata
        const extraOrders = await orderService.getOrdineExtra(formattedDate);
        if (extraOrders.length === 0) {
          setMappedOrders((prevOrders) =>
            prevOrders.map((order) => ({
              ...order,
              quantita: order[dayOfSelectedDate], // Usa i dati del giorno della data selezionata
            }))
          );
        } else {
          const mappedOrders = extraOrders.map((order) =>
            applicaPrezzoCatalogo(mapApiExtraOrderToRow(order, { previewId: false }))
          );
          setMappedOrders(mappedOrders);
        }
      }
    } catch (err) {
      if (!aperturaChiusura) {
        showAlertGlobal("Errore durante il salvataggio della chiusura straordinaria.", "danger");
      } else {
        showAlertGlobal("Errore durante il caricamento degli ordini straordinari.", "danger");
      }
    } finally {
      setLoading(false); // Nascondi il simbolo di caricamento
    }
  };

  const handleDuplicateFromDate = async (sourceDate) => {
    if (!specificDate) return;

    const sourceYmd = normalizeOrderDateYmd(dateToItalianYmd(sourceDate)) || dateToItalianYmd(sourceDate);
    if (!eligibleDuplicateDates.has(sourceYmd)) return;

    setLoading(true);
    try {
      const sourceOrders = await orderService.getOrdineExtra(sourceYmd);
      if (sourceOrders.length === 0) return;

      setMappedOrders(
        sourceOrders.map((order) =>
          applicaPrezzoCatalogo(mapApiExtraOrderToRow(order, { previewId: false }))
        )
      );
      setShowDuplicateModal(false);
    } catch {
      // duplicazione silenziosa: nessun alert
    } finally {
      setLoading(false);
    }
  };


  // Funzione per caricare le date evidenziate
  const loadHighlightedDates = useCallback(async () => {
    try {
      const allExtraOrders = await orderService.getOrdineExtra();

      const dateQuantities = allExtraOrders.reduce((acc, order) => {
        const date = normalizeOrderDateYmd(order.data_ord);
        if (!date) return acc;
        acc[date] = (acc[date] || 0) + Number(order.quanti);
        return acc;
      }, {});

      const allDates = Object.keys(dateQuantities);
      const zeroDates = allDates.filter((date) => dateQuantities[date] === 0);
      const activeDates = allDates.filter((date) => dateQuantities[date] > 0);

      setHighlightedDates(allDates);
      setZeroQuantityDates(zeroDates);
      setExtraOrderActiveDates(activeDates);
    } catch {
      setHighlightedDates([]);
      setZeroQuantityDates([]);
      setExtraOrderActiveDates([]);
    }
  }, []);

  // Funzione per eliminare la chiusura straordinaria
  const handleEliminaChiusura = async () => {
    await orderService.eliminaExtra([selectedZeroDate]);
    setShowChiusuraModal(false);
    setSelectedZeroDate(null);
    setSpecificDate(null); // Resetta la data specifica per tornare agli ordini ordinari
    setSpecificDateDay(null); // Resetta il giorno della data selezionata
    setSelectedDate(getItalianDate()); // Ripristina la data di default
    loadOrders();
  };

  // Funzione per creare ordine straordinario
  const handleCreaOrdineStraordinario = async () => {
    // Calcola il giorno della settimana dalla data selezionata
    const dateParts = selectedZeroDate.split('-');
    const selectedDateObj = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
    const dayOfSelectedDate = getDayOfWeekFromDate(selectedDateObj);
    setSpecificDateDay(dayOfSelectedDate);
    
    // Imposta la data specifica
    setSpecificDate(selectedDateObj);
    
    const extraOrders = await orderService.getOrdineExtra(selectedZeroDate);
    const mappedOrders = extraOrders.map((order) =>
      applicaPrezzoCatalogo(mapApiExtraOrderToRow(order, { previewId: false }))
    );
    setMappedOrders(mappedOrders);
    setShowChiusuraModal(false);
  };


  // Funzione per gestire la modifica della quantità di un articolo
  // Questa funzione viene chiamata quando si modifica la quantità di un articolo
  const handleQuantityChange = (articleId, newQuantity) => {
    if (planningTomorrowModificationsDisabled) {
      showAlertGlobal(MSG_FASCIA_DOMANI, 'warning');
      return;
    }
    const orderRow = mappedOrders.find((o) => o.id === articleId);
    if (
      orderRow &&
      typeof window !== 'undefined' &&
      localStorage.getItem('isSuperuser') !== 'true' &&
      specificDate
    ) {
      const d = dateToItalianYmd(specificDate);
      if (isDeliveryYmdLockedByOreBloccoProd(d, orderRow, orarioValido)) {
        showAlertGlobal(MSG_ARTICLE_MIN_PROD, 'warning');
        return;
      }
    }
  setMappedOrders((prevOrders) => {
    const updatedOrders = prevOrders.map((order) =>
      order.id === articleId ? { ...order, quantita: newQuantity } : order
    );
    return updatedOrders;
  });
};


  // questa sezione serve per caricare le date evidenziate quando il componente viene montato
  // e per caricare gli ordini all'apertura del componente
  // Inoltre, aggiorna la tabella degli ordini quando cambia il giorno selezionato
  // e carica gli articoli all'apertura del componente
  // Carica le date evidenziate quando il componente viene montato
  // Carica ordini e articoli all'apertura del componente
  // Aggiorna la tabella degli ordini quando cambia il giorno selezionato
  // Carica le date evidenziate quando il componente viene montato

  useEffect(() => {

    
    // Verifica se i dati del context sono stati caricati
    const dataLoaded = (destinazione && Object.keys(dayActivationFlags).length > 0) || 
                      (destinazione?.datiDestinazione && Object.keys(destinazione.datiDestinazione).length > 0);
    

    setContextDataLoaded(dataLoaded);
  }, [dayActivationFlags, destinazione]);

  useEffect(() => {
    if (destinazione) {
      loadHighlightedDates();
    }
  }, [destinazione, loadHighlightedDates]);

  useEffect(() => {
    if (!destinazione) return; // Carica gli ordini solo se destinazione è disponibile
    if (noPlanning === null) return; // Attende la lettura del configuratore PANIFYNOPLANNING
    if (noPlanning) {
      // Clic giorno -> apre direttamente la data specifica più vicina (nessun planning)
      openWeekdayAsSpecificDate(selectedDay);
    } else {
      loadOrders();
    }
  }, [destinazione, selectedDay, noPlanning, loadOrders, openWeekdayAsSpecificDate]);

  useEffect(() => {
    if (destinazione) {
      loadArticles(); // Carica gli articoli solo se destinazione è disponibile
    }
  }, [destinazione, loadArticles]);

  useEffect(() => {
    setMappedOrders((prev) => prev.map(applicaPrezzoCatalogo));
    setPreviewExtraOrdini((prev) => prev.map(applicaPrezzoCatalogo));
  }, [catalogoPrezziPerCodice, applicaPrezzoCatalogo]);

  // Effect per aggiornare i colori dei giorni quando cambiano gli ordini
  useEffect(() => {
    // Aggiorna i colori solo se gli ordini sono stati effettivamente caricati
    // Non durante il loading iniziale quando mappedOrders è ancora []
  }, [mappedOrders, loadingOrders]);

  useEffect(() => {
    // Aggiorna le quantità solo per ordini ordinari, non per ordini straordinari
    if (!specificDate) {
      setMappedOrders((prevOrders) =>
        prevOrders.map((order) => ({
          ...order,
          quantita: order[selectedDay], // Sostituisci il campo "quantita" con il valore del giorno selezionato
        }))
      );
    }
  }, [selectedDay, specificDate]);



  return (
    <Container fluid={isMobile} className={isMobile ? 'planning-mobile-page px-0' : 'mt-5'} style={{ paddingBottom: isMobile ? 'calc(var(--bottom-nav) + 24px)' : '80px' }}>
      
      {/* Barra sticky in alto: pulsanti + ricerca descrizione (senza riquadro) */}
      <div 
        className={`sticky-top planning-sticky-actions ${isMobile ? 'py-1' : 'py-2 px-2'}`} 
        style={{ zIndex: 1020, top: isMobile ? undefined : '60px', background: darkMode ? '#121212' : '#FFFEF9' }}
      >
        <div className="d-flex justify-content-between flex-wrap align-items-center">
          {specificDate ? (
            <>
              {/* Pulsanti per Annulla e Salva Ordine Straordinario */}
              <div className={isMobile ? 'planning-extra-actions' : 'd-flex justify-content-start'}>
                <Button
                  variant="danger"
                  className={isMobile ? undefined : 'ms-2'}
                  disabled={planningTomorrowModificationsDisabled}
                  onClick={() => {
                    handleCancelExtraOrder(specificDate); // Richiama la funzione per annullare l'ordine straordinario
                    if (noPlanning) {
                      // Resta sul giorno selezionato e riapre la data (ora svuotata)
                      reloadView();
                    } else {
                      setSpecificDate(null); // Resetta la data specifica
                      setSpecificDateDay(null); // Resetta il giorno della data selezionata
                      setSelectedDate(new Date()); // Ripristina la data di default
                      loadOrders(); // Ricarica i dati della tabella di default
                    }
                  }}
                >
                  <i className={`fas fa-times-circle ${isMobile ? 'me-1' : 'me-2'}`}></i>
                  {isMobile ? 'Annulla' : 'Annulla Ordine Straordinario'}
                </Button>
                <Button
                  variant="success"
                  className={isMobile ? undefined : 'ms-2'}
                  disabled={planningTomorrowModificationsDisabled}
                  onClick={async () => {
                    if (!specificDate) {
                      return;
                    }


                    try {
                      // Formatta la data in formato "YYYY-MM-DD"
                      const formattedDate = specificDate.toLocaleDateString("it-IT", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      }).split("/").reverse().join("-");

                      // Prepara il body per la richiesta
                      const body = {
                        ordini: [
                          {
                            data_ord: formattedDate,
                            articoli: mappedOrders.map((order) => ({
                              cod_arti: order.codice,
                              descriz_art: composeDescrizArt(order.descriz, order.nota),
                              prezzo: order.prezzo || 0,
                              quanti: order.quantita || 0,
                            })),
                          },
                        ],
                        elimina_non_presenti: true, // Imposta il flag come richiesto
                      };

                   

                      // Effettua la chiamata POST utilizzando salvaExtra
                      await orderService.salvaExtra(body.ordini, body.elimina_non_presenti);
                      await loadHighlightedDates();

                      // Mostra un messaggio di successo
                      showAlertGlobal("Ordine straordinario salvato con successo!", "success");

                      // noPlanning: ricarica la stessa data; altrimenti torna agli ordini ordinari
                      if (noPlanning) {
                        reloadView();
                      } else {
                        setSpecificDate(null); // Resetta la data specifica
                        setSpecificDateDay(null); // Resetta il giorno della data selezionata
                        loadOrders(); // Ricarica gli ordini ordinari
                      }
                    } catch (error) {
                      showAlertGlobal("Errore durante il salvataggio dell'ordine straordinario", "danger");
                    }
                  }}
                >
                  <i className={`fas fa-save ${isMobile ? 'me-1' : 'me-2'}`}></i>
                  {isMobile ? 'Salva' : 'Salva Ordine Straordinario'}
                </Button>

                {!noPlanning && (
                  <Button
                    variant="primary"
                    className={isMobile ? undefined : 'ms-2'}
                    onClick={() =>  {
                        setSpecificDate(null); // Resetta la data specifica
                        setSpecificDateDay(null); // Resetta il giorno della data selezionata
                        setSelectedDate(new Date()); // Ripristina la data di default
                        loadOrders(); // Ricarica i dati della tabella di default
                      }}
                    >
                    <i className={`fas fa-plus-circle ${isMobile ? 'me-1' : 'me-2'}`}></i>
                    {isMobile ? 'Torna al planning' : 'Torna al planning Settimanale'}
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              {(() => {
                    let isDayCurrentlyActive = false;
                    if (destinazione?.datiDestinazione) {
                      const dayMapping = {
                        lunedi: 'lun_act',
                        martedi: 'mar_act',
                        mercoledi: 'mer_act',
                        giovedi: 'gio_act',
                        venerdi: 'ven_act',
                        sabato: 'sab_act',
                        domenica: 'dom_act',
                      };
                      const dayField = dayMapping[selectedDay];
                      if (dayField) {
                        isDayCurrentlyActive = destinazione.datiDestinazione[dayField] === '0';
                      }
                    }
                    const extraBtn = (
                      <Button
                        variant="primary"
                        onClick={() => loadHighlightedDates() && handleOpenModal(true)}
                        className="w-100 planning-straordinari-btn"
                        disabled={planningTomorrowModificationsDisabled}
                      >
                        <i className="fas fa-plus-circle me-1"></i>
                        {isMobile ? 'Inserisci ordini straordinari' : 'Aggiungi / Modifica Ordini Straordinari'}
                      </Button>
                    );
                    const chiusuraBtn = (
                      <Button
                        variant="danger"
                        onClick={() => handleOpenModal(false)}
                        className="w-100"
                        disabled={planningTomorrowModificationsDisabled}
                      >
                        <i className="fas fa-calendar-times me-1"></i>
                        {isMobile ? 'Chiusura straordinaria' : 'Aggiungi chiusura Straordinaria'}
                      </Button>
                    );
                    const giornoBtn = !contextDataLoaded ? (
                      <Button variant="secondary" className="w-100" disabled>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Caricamento...
                      </Button>
                    ) : (
                      <Button
                        variant={isDayCurrentlyActive ? 'danger' : 'success'}
                        onClick={handleSuspendOrders}
                        className="w-100"
                        disabled={planningTomorrowModificationsDisabled}
                      >
                        {isDayCurrentlyActive ? (
                          <>
                            <i className="fas fa-ban me-1"></i>
                            {isMobile ? 'Disattiva giorno' : `Disattiva giorno ${selectedDay}`}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check me-1"></i>
                            {isMobile ? 'Riattiva giorno' : `Riattiva giorno ${selectedDay}`}
                          </>
                        )}
                      </Button>
                    );

                    if (isMobile) {
                      return (
                        <div className="d-grid gap-1 w-100">
                          {extraBtn}
                          <div className="planning-action-split">
                            {chiusuraBtn}
                            {giornoBtn}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div className="d-flex flex-column flex-lg-row gap-2 w-100">
                        <div className="d-grid d-lg-block">{extraBtn}</div>
                        <div className="d-grid gap-2 d-lg-flex justify-content-lg-end flex-grow-1">
                          {chiusuraBtn}
                          {giornoBtn}
                        </div>
                      </div>
                    );
                  })()}
              {(() => {
                // Solo mostra il messaggio se i dati sono stati caricati
                if (!contextDataLoaded) {
                  return null;
                }
                
                // Determina se il giorno è attualmente disattivo (1 = disattivo, 0 = attivo)
                let isDayCurrentlyDisabled = false;
                
                if (dayActivationFlags && Object.keys(dayActivationFlags).length > 0) {
                  isDayCurrentlyDisabled = dayActivationFlags[selectedDay] === '1';
                } else if (destinazione?.datiDestinazione) {
                  const dayMapping = {
                    'lunedi': 'lun_act',
                    'martedi': 'mar_act',
                    'mercoledi': 'mer_act',
                    'giovedi': 'gio_act',
                    'venerdi': 'ven_act',
                    'sabato': 'sab_act',
                    'domenica': 'dom_act'
                  };
                  const dayField = dayMapping[selectedDay];
                  if (dayField) {
                    isDayCurrentlyDisabled = destinazione.datiDestinazione[dayField] === '1';
                  }
                }
                
                return (
                  <>
                    {isDayCurrentlyDisabled && (
                      <p className="text-danger fw-bold mt-2">
                        Giorno {selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)} disattivato.
                      </p>
                    )}
                    {calendarBlocksTodayTomorrowItaly && hasTimeRestrictionConfig && (
                      <p className={isMobile ? 'text-warning planning-fascia-msg' : 'text-warning fw-bold mt-2'}>
                        <i className="fas fa-clock me-1"></i>
                        Fascia oraria attiva (ore {fasciaCloseLabel}
                        {' – '}
                        {useCustomBlockWindow
                          ? formatConfiguratorOrarioDisplay(orarioSblocco)
                          : '08:00'}
                        ): non è possibile selezionare{' '}
                        <strong>oggi</strong> né <strong>domani</strong>.
                      </p>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>

        {/* Campo ricerca: posiziona la vista sulla riga del prodotto con la descrizione corrispondente */}
        {!loadingOrders && mappedOrders.length > 0 && (
          <div className="mt-2">
            <Form.Group className="mb-0">
              <Form.Control
                type="text"
                placeholder="Cerca prodotto per descrizione..."
                value={cercaDescrizione}
                onChange={(e) => setCercaDescrizione(e.target.value)}
                className={darkMode ? "bg-dark text-light border-secondary" : ""}
                size="sm"
                aria-label="Cerca per descrizione"
              />
            </Form.Group>
          </div>
        )}

        {/* Dissolvenza gradiente: la lista sottostante sfuma scorrendo sotto la barra di ricerca */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            transform: 'translateY(100%)',
            height: isMobile ? 18 : 26,
            pointerEvents: 'none',
            background: `linear-gradient(to bottom, ${darkMode ? '#121212' : '#FFFEF9'} 0%, ${darkMode ? 'rgba(18,18,18,0)' : 'rgba(255,254,249,0)'} 100%)`,
          }}
        />
      </div>

      {/* Mostra il calendario o il messaggio in base alla presenza di ordini straordinari */}
      {((!isMobile && highlightedDates.length === 0) || specificDate) && (
      <div className={isMobile ? 'mt-2' : 'mt-4'}>
        {!isMobile && highlightedDates.length === 0 && (
          <p>Non sono presenti ordini straordinari nel mese corrente.</p>
        )}
        {specificDate && (
          <>
            {/* Titolo dinamico sopra i pulsanti */}
            <h3 className="mt-4 text">
              {specificDate
                ? `Quantità ordinata esclusivamente per il giorno ${specificDate.toLocaleDateString("it-IT", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}`
                : `Quantità ordinata per ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}`}
            </h3>
          </>
        )}
      </div>
      )}



      <div ref={ordersDockRef} className={isMobile ? 'planning-orders-dock' : undefined}>
      {(() => {
        // Determina se il giorno è disattivato
        let isDayDisabled = false;
        
        if (contextDataLoaded) {
          if (dayActivationFlags && Object.keys(dayActivationFlags).length > 0) {
            isDayDisabled = dayActivationFlags[selectedDay] === '1';
          } else if (destinazione?.datiDestinazione) {
            const dayMapping = {
              'lunedi': 'lun_act',
              'martedi': 'mar_act',
              'mercoledi': 'mer_act',
              'giovedi': 'gio_act',
              'venerdi': 'ven_act',
              'sabato': 'sab_act',
              'domenica': 'dom_act'
            };
            const dayField = dayMapping[selectedDay];
            if (dayField) {
              isDayDisabled = destinazione.datiDestinazione[dayField] === '1';
            }
          }
        }

        // Se il giorno è disattivato e non è un ordine straordinario, non mostrare la tabella
        if (isDayDisabled && !specificDate) {
          return (
            <div className="text-center py-5">
              <div className="alert alert-warning" role="alert">
                <i className="fas fa-ban me-2"></i>
                <strong>Giorno {selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)} disattivato</strong>
                <br />
                Gli ordini per questo giorno sono sospesi.
              </div>
            </div>
          );
        }

        // Resto della logica normale per giorno attivo o ordini straordinari
        if (loadingOrders) {
          return (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Caricamento ordini...</span>
              </Spinner>
            </div>
          );
        }

        if (errorOrders) {
          return <Alert variant="danger">{errorOrders}</Alert>;
        }

        if (mappedOrders.length === 0) {
          return (
            <>
              <p className="text-center">Nessun articolo per {selectedDay}.</p>
            </>
          );
        }

        return (
          <>
            <div style={{ marginTop: isMobile ? '0.5rem' : '50px' }}>
              {/* Titolo dinamico sopra la tabella */}
              <h3 className={isMobile ? 'mt-2 text' : 'mt-4 text'}>
                {specificDate
                  ? null
                  : `Quantità ordinata per ${selectedDay.charAt(0).toUpperCase() + selectedDay.slice(1)}`
                }
              </h3>
              
              {allOrdersSuspended && (
                <p className="text-center text-muted">
                  Per riattivare l'ordine nel giorno selezionato clicca sulla quantità del singolo prodotto.
                </p>
              )}

              {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 100 }}>
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : (
                <OrderTable
                  ordini={hideZeroQuantity ? 
                    mappedOrders.filter(order => {
                      if (specificDate) {
                        // ORDINI STRAORDINARI: Filtra per quantita > 0
                        const quantity = order.quantita || 0;
                        return Number(quantity) > 0;
                      } else {
                        // ORDINI ORDINARI: Filtra per currentDayQuantity > 0 (comportamento esistente)
                        const currentDayQuantity = order[selectedDay] || 0;
                        return Number(currentDayQuantity) > 0;
                      }
                    }) : 
                    mappedOrders
                  }
                  dayName={specificDate ? (specificDateDay || selectedDay) : selectedDay}
                  onQuantityChange={handleQuantityChange}
                  onSavePlanning={handleSavePlanning}
                  isQuantityEditable={!!specificDate}
                  setMappedOrders={setMappedOrders}
                  isActionsDisabled={planningTomorrowModificationsDisabled}
                  readOnlyWeekdayKeys={readOnlyWeekdayKeysForModal}
                  getExtraReadOnlyKeysForArticle={getExtraReadOnlyKeysForArticleCb}
                  getRowLockedForTodayTomorrow={getRowLockedForTodayTomorrowCb}
                  dayActivationFlags={dayActivationFlags}
                  destinazione={destinazione}
                  cercaDescrizione={cercaDescrizione}
                />
              )}
            </div>



          </>
        );
      })()}

      {isMobile && toolbarPinnedTop && (
        <div aria-hidden="true" style={{ height: toolbarHeight }} />
      )}

      {/* Pulsanti prodotti: in basso sulla lista del giorno, poi si ancorano in alto */}
      <div 
        ref={productToolbarRef}
        className={`${isMobile ? `planning-product-toolbar${toolbarPinnedTop ? ' is-pinned-top' : ''}` : 'd-flex gap-2 justify-content-md-start py-2 px-3 mt-3'}`}
        style={{ 
          position: isMobile ? undefined : 'sticky',
          bottom: isMobile ? undefined : 0,
          zIndex: isMobile ? undefined : 1020,
          background: darkMode ? '#121212' : '#FFFEF9'
        }}
      >
        <Button
          variant="primary"
          onClick={() => setShowAddProductsModal(true)}
          disabled={planningTomorrowModificationsDisabled}
        >
          <i className="fas fa-plus-circle me-1"></i>{isMobile ? 'Aggiungi prodotto' : 'Aggiungi Prodotti'}
        </Button>
        
        <Button
          variant={hideZeroQuantity ? "warning" : "outline-secondary"}
          onClick={() => setHideZeroQuantity(!hideZeroQuantity)}
          title={hideZeroQuantity ? "Mostra articoli a 0" : "Nascondi articolo a 0"}
        >
          <i className={`fas ${hideZeroQuantity ? "fa-eye" : "fa-eye-slash"} me-2`}></i>
          {hideZeroQuantity
            ? (isMobile ? 'Mostra articoli a 0' : 'Visualizza q.tà 0')
            : (isMobile ? 'Nascondi articoli a 0' : 'Nascondi q.tà 0')}
        </Button>

        {filteredArticles.filter(a => a.in_evidenza === true).length > 0 && (
          <Button
            variant="warning"
            onClick={() => document.getElementById('articoli-in-evidenza')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            title="Vai agli articoli in promozione"
          >
            <i className="fas fa-tag me-1"></i>{isMobile ? 'Visualizza promozioni' : 'Mostra articoli in Promozione'}
          </Button>
        )}

        {specificDate && (
          <Button
            variant="outline-primary"
            onClick={async () => {
              await loadHighlightedDates();
              setShowDuplicateModal(true);
            }}
            disabled={planningTomorrowModificationsDisabled || !hasEligibleDuplicateDates}
            title={
              planningTomorrowModificationsDisabled
                ? MSG_FASCIA_DOMANI
                : hasEligibleDuplicateDates
                  ? 'Copia articoli da un ordine personalizzato precedente'
                  : 'Nessun ordine personalizzato nei giorni precedenti rispetto a questa data'
            }
          >
            <i className="fas fa-copy me-2"></i>Duplica da altro giorno
          </Button>
        )}
      </div>
      </div>

      <div
        className={isMobile ? 'planning-scroll-cover' : undefined}
        style={isMobile ? { background: darkMode ? '#121212' : '#FFFEF9' } : undefined}
      >
      {/* Anteprima ordini straordinari per la data di consegna del giorno selezionato (solo lettura) */}
      {!specificDate &&
        !loadingOrders &&
        previewExtraOrdini.length > 0 &&
        previewExtraDeliveryYmd && (
          <div className="px-2 px-md-3">
            <h3 className="mt-4">
              Articoli straordinari — consegna:{' '}
              {formatItalianLongDateFromYmd(previewExtraDeliveryYmd)}
            </h3>
            <OrderTable
              ordini={previewExtraOrdini}
              dayName={selectedDay}
              onSavePlanning={() => {}}
              onQuantityChange={() => {}}
              isQuantityEditable={false}
              hideRowActions
              isActionsDisabled
              readOnlyWeekdayKeys={readOnlyWeekdayKeysForModal}
              getExtraReadOnlyKeysForArticle={getExtraReadOnlyKeysForArticleCb}
              getRowLockedForTodayTomorrow={null}
              dayActivationFlags={dayActivationFlags}
              destinazione={destinazione}
              cercaDescrizione=""
            />
          </div>
        )}

      {/* ARTICOLI IN EVIDENZA - SEMPRE VISIBILI */}
      {loadingArticles ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Caricamento articoli...</span>
          </Spinner>
        </div>
      ) : errorArticles ? (
        <Alert variant="danger">{errorArticles}</Alert>
      ) : articles.length === 0 ? (
        <p className="text-center">Nessun articolo disponibile.</p>
      ) : (
        <>
          {filteredArticles.filter(a => a.in_evidenza === true).length > 0 && (
            <div id="articoli-in-evidenza">
              <h3 className={isMobile ? 'h6 mt-3 mb-2' : 'mt-4'}>Articoli in evidenza per questo mese</h3>
              <ArticleTable
                articoli={filteredArticles}
                inEvidenza={true}
                handleSaveArticle={handleSavePlanning}
                specificDate={specificDate}
                mappedOrders={mappedOrders}
                setMappedOrders={setMappedOrders}
                isActionsDisabled={planningTomorrowModificationsDisabled}
                readOnlyWeekdayKeys={readOnlyWeekdayKeysForModal}
                getExtraReadOnlyKeysForArticle={getExtraReadOnlyKeysForArticleCb}
                getRowLockedForTodayTomorrow={getRowLockedForTodayTomorrowCb}
                currentDay={specificDate ? null : selectedDay}
                mostraPrezzi={mostraPrezzi}
              />
            </div>
          )}
        </>
      )}
      </div>

      {/* Modal per aggiungere un ordine straordinario */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal} 
        centered
        fullscreen={isMobile || undefined}
        data-bs-theme={darkMode ? "dark" : "light"}
      >
        <Modal.Header closeButton>
         <Modal.Title>
          {aperturaChiusura
            ? "Seleziona una data per Apertura Straordinaria"
            : "Seleziona una data per Chiusura Straordinaria"}
        </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 350 }}>
            <Calendar
              onChange={(selectedDate) => {
                handleDateChange(selectedDate); // Passa la data selezionata
              }}
              value={specificDate || selectedDate}
              tileDisabled={({ date, view }) => {
                if (view !== 'month') return false;
                if (!calendarBlocksTodayTomorrowItaly) return false;
                const ymd = dateToItalianYmd(date);
                const t0 = dateToItalianYmd(new Date());
                const t1 = addDaysToYmd(t0, 1);
                return ymd === t0 || ymd === t1;
              }}
              tileClassName={({ date, view }) => {
                if (view === "month") {
                  const formattedDate = dateToItalianYmd(date);

                  if (zeroQuantityDates.includes(formattedDate)) {
                    return "zero-quantity-date";
                  }
                  if (highlightedDates.includes(formattedDate)) {
                    return "highlighted-date";
                  }
                }
                return null;
              }}
            />
          </div>
        </Modal.Body>
        {/* Legenda */}
          <div className="mb-3">
            <div className="d-flex align-items-center mb-1">
              <span style={{
                display: "inline-block",
                width: 18,
                height: 18,
                background: "yellow",
                border: "1px solid #ccc",
                marginRight: 8
              }} />
              <span>Ordine Straordinario impostato → data evidenziata in <b>giallo</b></span>
            </div>
            <div className="d-flex align-items-center mb-1">
              <span style={{
                display: "inline-block",
                width: 18,
                height: 18,
                background: "red",
                border: "1px solid #ccc",
                marginRight: 8
              }} />
              <span>Sospensione straordinaria → data evidenziata in <b>rosso</b></span>
            </div>
            <div className="d-flex align-items-center mb-1">
              <span style={{
                display: "inline-block",
                width: 18,
                height: 18,
                background: "transparent",
                border: "1px solid #ccc",
                marginRight: 8
              }} />
              <span>Nessun ordine straordinario → nessuna evidenziazione</span>
            </div>
          </div>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            <i className="fas fa-times me-2"></i>Annulla
          </Button>
        </Modal.Footer>
      </Modal>



      {/* Inserisci qui il nuovo modal per la chiusura straordinaria */}
      <Modal 
        show={showChiusuraModal} 
        onHide={() => setShowChiusuraModal(false)} 
        centered
        fullscreen={isMobile || undefined}
        data-bs-theme={darkMode ? "dark" : "light"}
      >
        <Modal.Header closeButton>
          <Modal.Title>Chiusura Straordinaria</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Hai una chiusura straordinaria per il giorno selezionato.<br />
          Cosa vuoi fare?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleEliminaChiusura}>
            Elimina chiusura straordinaria
          </Button>
          <Button variant="primary" onClick={handleCreaOrdineStraordinario}>
            Crea Ordine straordinario
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDuplicateModal}
        onHide={() => setShowDuplicateModal(false)}
        centered
        fullscreen={isMobile || undefined}
        data-bs-theme={darkMode ? 'dark' : 'light'}
      >
        <Modal.Header closeButton>
          <Modal.Title>Seleziona il giorno da duplicare</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!hasEligibleDuplicateDates ? (
            <p className="text-muted mb-0 text-center">
              Nessun ordine personalizzato nei giorni precedenti.
            </p>
          ) : (
            <>
              <p className="small text-muted">
                Sono selezionabili solo i giorni precedenti con ordine personalizzato. La tabella
                del giorno corrente verrà sostituita; potrai modificare e salvare manualmente.
              </p>
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 350 }}>
                <Calendar
                  onChange={(selectedSourceDate) => {
                    handleDuplicateFromDate(selectedSourceDate);
                  }}
                  value={specificDate}
                  tileDisabled={({ date, view }) => {
                    if (view !== 'month') return false;
                    return !eligibleDuplicateDates.has(dateToItalianYmd(date));
                  }}
                  tileClassName={({ date, view }) => {
                    if (view !== 'month') return null;
                    const ymd = dateToItalianYmd(date);
                    if (eligibleDuplicateDates.has(ymd)) return 'highlighted-date';
                    return null;
                  }}
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDuplicateModal(false)}>
            <i className="fas fa-times me-2"></i>Annulla
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal per aggiungere articoli */}
      <Modal 
        show={showAddProductsModal} 
        onHide={() => setShowAddProductsModal(false)} 
        centered size="xl"
        fullscreen={isMobile || undefined}
        data-bs-theme={darkMode ? "dark" : "light"}
      >
        <Modal.Header closeButton>
          <Modal.Title>Seleziona articoli da aggiungere</Modal.Title>
        </Modal.Header>
        {/* FILTRI */}
          <div className="container-fluid px-4 pt-2">
            <div className="row g-2 align-items-center mb-3">
              <div className="col-md-6 d-flex align-items-center">
                <span className="me-2 text-secondary">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Filtra per codice o descrizione"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
              </div>
              <div className="col-md-4 d-flex align-items-center">
                <span className="me-2 text-secondary">
                  <i className="fas fa-tags"></i>
                </span>
                <select
                  className="form-select"
                  value={selectedCategoria}
                  onChange={e => setSelectedCategoria(e.target.value)}
                >
                  <option value="">Tutte le categorie</option>
                  {categorie.map(cat => (
                    <option key={cat.codice} value={cat.codice}>
                      {cat.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <ArticleTable
            articoli={filteredArticles}
            inEvidenza={false}
            handleSaveArticle={handleSavePlanning}
            specificDate={specificDate}
            mappedOrders={mappedOrders} // Passa gli ordini mappati per il confronto
            setMappedOrders={setMappedOrders}
            isActionsDisabled={planningTomorrowModificationsDisabled}
            readOnlyWeekdayKeys={readOnlyWeekdayKeysForModal}
            getExtraReadOnlyKeysForArticle={getExtraReadOnlyKeysForArticleCb}
            getRowLockedForTodayTomorrow={getRowLockedForTodayTomorrowCb}
            currentDay={specificDate ? null : selectedDay}
            mostraPrezzi={mostraPrezzi}
          />
       </Modal.Body>
          {/* ...resto del calendario... */}
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddProductsModal(false)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}

export default Planning;
